import { Policies, policyDescriptions } from "@fena/toolkit-types";
import { AppRoutes } from "./routes";

export interface PolicyItem {
  label: string;
  description: string;
  value: Policies.PolicyKeys;
  subItems: Array<{
    label: string;
    description: string;
    value: Policies.PolicyKeys;
  }>;
}

type PolicyKeys =
  | typeof Policies.COMPANY
  | typeof Policies.COMPANY_PLAN
  | typeof Policies.COMPANY_BILLING
  | typeof Policies.USER
  | typeof Policies.TEAM
  | typeof Policies.ROLE
  | typeof Policies.PRODUCT
  | typeof Policies.CATEGORY
  | typeof Policies.BRAND
  | typeof Policies.SUPPLIER
  | typeof Policies.CUSTOMER
  | typeof Policies.ORDER
  | typeof Policies.API_KEY
  | typeof Policies.LOCATION
  | typeof Policies.STATISTICS
  | typeof Policies.PAYMENT
  | typeof Policies.INVOICE
  | typeof Policies.INTEGRATIONS
  | typeof Policies.INCOMING_INVENTORY;

const generatePolicySubItems = (
  policyEnum: PolicyKeys
): Array<{ label: string; value: string; description: string }> => {
  const subItems: Array<{ label: string; value: string; description: string }> =
    [];

  Object.keys(policyEnum).forEach((key) => {
    const enumValue = policyEnum[key as keyof typeof policyEnum];
    subItems.push({
      label: (policyDescriptions as Record<string, string>)[
        enumValue as Policies.PolicyKeys
      ],
      value: enumValue,
      description: "",
    });
  });

  return subItems.filter((obj) => !obj.value.includes("ADMIN_ACCESS"));
};

export const policiesAsTree = [
  {
    label: "Company",
    description: "Policies related to the company itself",
    value: Policies.COMPANY.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.COMPANY),
  },
  {
    label: "Company plan",
    description: "Current plan of your company",
    value: Policies.COMPANY_PLAN.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.COMPANY_PLAN),
  },
  {
    label: "Company billing",
    description: "Policies related to your companies' billing methods",
    value: Policies.COMPANY_BILLING.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.COMPANY_BILLING),
  },
  {
    label: "User",
    description: "Policies related to the user himself",
    value: Policies.USER.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.USER),
  },
  {
    label: "Team",
    description: "Policies related to team management",
    value: Policies.TEAM.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.TEAM),
  },
  {
    label: "Role",
    description: "Policies related to roles inside the company",
    value: Policies.ROLE.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.ROLE),
  },
  {
    label: "Products",
    description: "Policies related to the products section",
    value: Policies.PRODUCT.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.PRODUCT),
  },
  {
    label: "Category",
    description: "Policies related to the categories section",
    value: Policies.CATEGORY.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.CATEGORY),
  },
  {
    label: "Brand",
    description: "Policies related to the brands section",
    value: Policies.BRAND.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.BRAND),
  },
  {
    label: "Supplier",
    description: "Policies related to the suppliers section",
    value: Policies.SUPPLIER.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.SUPPLIER),
  },
  {
    label: "Customer",
    description: "Policies related to the customers section",
    value: Policies.CUSTOMER.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.CUSTOMER),
  },
  {
    label: "Order",
    description: "Policies related to the order section",
    value: Policies.ORDER.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.ORDER),
  },
  {
    label: "Invoice",
    description: "Policies related to the invoice section",
    value: Policies.INVOICE.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.INVOICE),
  },
  {
    label: "Incoming inventory",
    description: "Policies related to the incoming inventory section",
    value: Policies.INCOMING_INVENTORY.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.INCOMING_INVENTORY),
  },
  {
    label: "API key",
    description: "Policies related to API key management",
    value: Policies.API_KEY.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.API_KEY),
  },
  {
    label: "Location",
    description: "Policies related to location management",
    value: Policies.LOCATION.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.LOCATION),
  },
  {
    label: "Statistics",
    description: "Policies related to statistics dashboard",
    value: Policies.STATISTICS.VIEW_ALL,
    subItems: generatePolicySubItems(Policies.STATISTICS),
  },
  {
    label: "Payment",
    description: "Policies related to payments inside the system",
    value: Policies.PAYMENT.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.PAYMENT),
  },
  {
    label: "Integrations",
    description: "Policies related to the integrations section",
    value: Policies.INTEGRATIONS.ADMIN_ACCESS,
    subItems: generatePolicySubItems(Policies.INTEGRATIONS),
  },
];

export const routePolicies: Record<string, Policies.PolicyKeys[]> = {
  [AppRoutes.Private.Inventory.ORDERS_LIST]: [
    Policies.ORDER.ADMIN_ACCESS,
    Policies.ORDER.VIEW_ORDERS,
  ],
  [AppRoutes.Private.Payments.TRANSACTIONS_LIST]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Payments.FENA_PAYMENTS_LIST]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Payments.FENA_PAYMENTS_QR]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Payments.FENA_PAYMENTS_MOBILE]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Payments.PAYMENTS_CASH]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Payments.PAYMENTS_CARD]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Payments.PAYMENTS_OTHER]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Payments.RECURRING_PAYMENTS]: [
    Policies.PAYMENT.ADMIN_ACCESS,
    Policies.PAYMENT.VIEW_PAYMENTS,
  ],
  [AppRoutes.Private.Inventory.PRODUCT_LIST]: [
    Policies.PRODUCT.ADMIN_ACCESS,
    Policies.PRODUCT.VIEW_PRODUCTS,
  ],
  [AppRoutes.Private.Inventory.PRODUCT_LIST_STOCK]: [
    Policies.PRODUCT.ADMIN_ACCESS,
    Policies.PRODUCT.VIEW_PRODUCTS,
  ],
  [AppRoutes.Private.Inventory.CATEGORIES]: [
    Policies.CATEGORY.ADMIN_ACCESS,
    Policies.CATEGORY.VIEW_CATEGORIES,
  ],
  [AppRoutes.Private.Inventory.BRANDS]: [
    Policies.BRAND.ADMIN_ACCESS,
    Policies.BRAND.VIEW_BRANDS,
  ],
  [AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS]: [
    Policies.ORDER.ADMIN_ACCESS,
    Policies.ORDER.CREATE_ORDERS,
  ],
  [AppRoutes.Private.Inventory.INCOMING_INVENTORY]: [
    Policies.INCOMING_INVENTORY.ADMIN_ACCESS,
    Policies.INCOMING_INVENTORY.VIEW_INCOMING_INVENTORY,
  ],
  [AppRoutes.Private.Inventory.REJECTED_AND_MISSING]: [
    Policies.INCOMING_INVENTORY.ADMIN_ACCESS,
    Policies.INCOMING_INVENTORY.VIEW_INCOMING_INVENTORY,
  ],

  [AppRoutes.Private.Invoices.INVOICES_LIST]: [
    Policies.INVOICE.ADMIN_ACCESS,
    Policies.INVOICE.VIEW_INVOICES,
  ],
  [AppRoutes.Private.Invoices.COMMERCIAL_INVOICES_LIST]: [
    Policies.COMMERCIAL_INVOICES.ADMIN_ACCESS,
    Policies.COMMERCIAL_INVOICES.VIEW_COMMERCIAL_INVOICES,
  ],
  [AppRoutes.Private.Invoices.QUOTES_LIST]: [
    Policies.INVOICE.ADMIN_ACCESS,
    Policies.INVOICE.VIEW_INVOICES,
  ],
  [AppRoutes.Private.Customers.CUSTOMERS_LIST]: [
    Policies.CUSTOMER.ADMIN_ACCESS,
    Policies.CUSTOMER.VIEW_CUSTOMERS,
  ],
  [AppRoutes.Private.Suppliers.SUPPLIERS]: [
    Policies.SUPPLIER.ADMIN_ACCESS,
    Policies.SUPPLIER.VIEW_SUPPLIERS,
  ],
  [AppRoutes.Private.Shipments.SHIPMENTS_PICKING_LIST]: [
    Policies.WARHOUSE.ADMIN_ACCESS,
    Policies.WARHOUSE.VIEW_ORDER_TO_BE_PICKED,
  ],
  [AppRoutes.Private.Shipments.SHIPMENTS_DESPATCH_LIST]: [
    Policies.WARHOUSE.ADMIN_ACCESS,
    Policies.WARHOUSE.BOOK_CONSIGNMENTS,
    Policies.WARHOUSE.PRINT_DESPATCH_LABEL,
  ],
};
